import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ListingTable from "../components/ListingTable";
import ListingModal from "../components/ListingModal";
import {
  fetchListings,
  updateListingFromModal,
  syncListing,
  endListing,
  listOnEbay,
  scrapeMetadata,
  updatePriceOnly,
  bulkPriceIncrease,
  bulkUpdateCondition,
  scrapeListingPrice,
  applyLatestScrapedPrice,
  fetchTrainerInventoryOptions,
  markListingAsTrainer,
  syncListingsFromEbay,
} from "../services/listingApi";

const CARD_VARIANTS = [
  { value: "Normal", label: "Normal" },
  { value: "Reverse Holo", label: "Reverse Holo" },
  { value: "Holo", label: "Holo" },
];

const inferCardVariantFromListing = (row) => {
  const text = `${row?.listing_title || ""} ${row?.sku || ""}`.toLowerCase();
  if (text.includes("reverse holo") || text.includes("reverse-holo") || text.includes("reverse_holo")) {
    return "Reverse Holo";
  }
  return row?.trainer_inventory_card_variant || "Normal";
};

const isSoldOutListedRow = (row) => {
  const appStatus = row?.listing_status || "";
  const ebayStatus = row?.ebay_listing_status || "";
  const qty = row?.ebay_available_quantity;
  const numericQty = qty === null || qty === undefined || qty === "" ? null : Number(qty);
  return appStatus === "Listed" && (ebayStatus === "OUT_OF_STOCK" || numericQty === 0);
};

export default function ListingManager() {
  const [activeTab, setActiveTab] = useState("All");
  const [poFilter, setPoFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isEbayPullSyncing, setIsEbayPullSyncing] = useState(false);
  const [ebayPullSyncMessage, setEbayPullSyncMessage] = useState("");

  // modal state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editCondition, setEditCondition] = useState("");
  const [editListingFormat, setEditListingFormat] = useState("FIXED_PRICE");
  const [editListingDuration, setEditListingDuration] = useState("GTC");

  // listing progress + summary state
  const [isListingInProgress, setIsListingInProgress] = useState(false);
  const [listingProgress, setListingProgress] = useState({
    current: 0,
    total: 0,
  });
  const [listingSummaryOpen, setListingSummaryOpen] = useState(false);
  const [listingSummaryItems, setListingSummaryItems] = useState([]);

  const [trainerDialogOpen, setTrainerDialogOpen] = useState(false);
  const [trainerDialogRow, setTrainerDialogRow] = useState(null);
  const [trainerInventoryOptions, setTrainerInventoryOptions] = useState([]);
  const [trainerListingRole, setTrainerListingRole] = useState("single");
  const [trainerCardVariant, setTrainerCardVariant] = useState("Normal");
  const [selectedTrainerInventoryId, setSelectedTrainerInventoryId] = useState("");
  const [createNewTrainerRecord, setCreateNewTrainerRecord] = useState(false);
  const [trainerInventoryNotes, setTrainerInventoryNotes] = useState("");
  const [trainerDialogError, setTrainerDialogError] = useState("");
  const [trainerDialogSaving, setTrainerDialogSaving] = useState(false);

  const refresh = async () => {
    await fetchListings(setRows);
  };

  const handleScrapePrice = async (id) => {
    try {
      await scrapeListingPrice(id);

      // Safer flow: trigger scrape only. User manually clicks APPLY SCRAPED when ready.
      alert(
        `Scrape started for listing ${id}.\n` +
          `When the scrape finishes, click APPLY SCRAPED to pull the latest predicted value into the Scraped field.`
      );
    } catch (e) {
      alert(`Scrape start failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const handleApplyLatestScrapedPrice = async (id) => {
    try {
      const res = await applyLatestScrapedPrice(id);
      const p = res?.predicted_sale_price_scraped;
      alert(
        p !== undefined && p !== null
          ? `Updated scraped predicted price to ${p}`
          : "Applied latest scraped price"
      );
      await refresh();
    } catch (e) {
      alert(`Apply scraped price failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // FC255: existing Listings editor remains authoritative for a newly created
  // local draft. The optional deep link merely selects the saved listing.
  const fc255Opened = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const idText = params.get('fc255_listing_id');
    if (!/^\d+$/.test(idText || '') || fc255Opened.current === idText || !rows.length) return;
    const draft = rows.find(row => String(row.id) === idText);
    if (!draft) return;
    fc255Opened.current = idText;
    setActiveTab('All');
    setSearch(String(draft.sku || idText));
    setEditRowId(draft.id);
    setEditTitle(draft.listing_title || '');
    setEditPrice(draft.listing_price ?? '');
    setEditCondition(draft.listing_condition || 'Near Mint');
    setEditListingFormat(draft.listing_format || 'FIXED_PRICE');
    setEditListingDuration(draft.listing_duration || 'GTC');
    setEditDialogOpen(true);
    params.delete('fc255_listing_id');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash || ''}`);
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      const status = r.listing_status || "";

      const matchStatus =
        activeTab === "To Print"
          ? status === "To Print"
          : activeTab === "Photo'ing"
          ? ["Label Printed", "Photo Uploaded"].includes(status)
          : activeTab === "Ready to List"
          ? status === "Draft Ready"
          : activeTab === "Live"
          ? status === "Listed" && !isSoldOutListedRow(r)
          : activeTab === "Out of Stock"
          ? status === "Out of Stock" || isSoldOutListedRow(r)
          : true;

      const matchPO = poFilter
        ? String(r.purchase_order_id || "").includes(String(poFilter))
        : true;

      const q = search.trim().toLowerCase();
      const matchSearch = q
        ? String(r.sku || "").toLowerCase().includes(q) ||
          String(r.listing_title || "").toLowerCase().includes(q)
        : true;

      return matchStatus && matchPO && matchSearch;
    });
  }, [rows, activeTab, poFilter, search]);

  // totals for current grid contents (after PO filter, tab, search)
  const { totalBuyPrice, totalExpectedProfit } = useMemo(() => {
    return visibleRows.reduce(
      (acc, row) => {
        const buy = Number(row.agreed_price) || 0;
        const profit = Number(row.expected_profit) || 0;
        acc.totalBuyPrice += buy;
        acc.totalExpectedProfit += profit;
        return acc;
      },
      { totalBuyPrice: 0, totalExpectedProfit: 0 }
    );
  }, [visibleRows]);

  // ===== Bulk actions =====

  const runEbayPullSync = async ({ limit, filterMode, listingIds = [], label }) => {
    const selectedCount = Array.isArray(listingIds) ? listingIds.length : 0;
    const targetText = selectedCount ? `${selectedCount} selected listing(s)` : `${limit} listing(s)`;
    const confirmed = window.confirm(
      `${label || "Sync listings from eBay"}?\n\nThis is eBay → app only. It will pull current eBay offer/listing data into local sync fields and snapshot history. It will not push app changes to eBay.\n\nTarget: ${targetText}`
    );
    if (!confirmed) return;

    setIsEbayPullSyncing(true);
    setEbayPullSyncMessage("Syncing current eBay listing data...");
    try {
      const res = await syncListingsFromEbay({
        limit,
        filterMode,
        listingIds,
      });
      const message =
        `Attempted ${res?.attempted ?? 0}; refreshed ${res?.refreshed ?? 0}; failed ${res?.failed ?? 0}.`;
      setEbayPullSyncMessage(message);
      alert(message);
      await refresh();
    } catch (e) {
      const message = e?.response?.data?.error || e.message || "eBay pull sync failed";
      setEbayPullSyncMessage(message);
      alert(`eBay pull sync failed: ${message}`);
    } finally {
      setIsEbayPullSyncing(false);
    }
  };

  const handleSyncSelectedFromEbay = async () => {
    if (!selectedIds.length) {
      alert("Select at least one listed row to sync from eBay.");
      return;
    }
    await runEbayPullSync({
      limit: selectedIds.length,
      filterMode: "listed",
      listingIds: selectedIds,
      label: "Sync selected listings from eBay",
    });
  };

  // Lists each selected item to eBay with a single progress window + final summary
  const handleListSelected = async () => {
    if (!selectedIds.length) {
      alert("Select at least one row.");
      return;
    }

    const confirm = window.confirm(
      `List ${selectedIds.length} selected listing(s) to eBay?`
    );
    if (!confirm) return;

    const total = selectedIds.length;
    setIsListingInProgress(true);
    setListingProgress({ current: 0, total });
    setListingSummaryItems([]);

    const results = [];

    for (let i = 0; i < selectedIds.length; i++) {
      const id = selectedIds[i];
      const row = rows.find((r) => r.id === id);
      const title = row?.listing_title || `Listing #${id}`;

      try {
        // IMPORTANT: silent mode so we don't see per-listing alerts
        await listOnEbay(id, { silent: true });
        results.push({
          id,
          title,
          status: "success",
          message: "Listed successfully on eBay.",
        });
      } catch (e) {
        const message =
          e?.response?.data?.error || e?.message || "Failed to list on eBay.";
        console.error("List on eBay failed for id", id, e);
        results.push({
          id,
          title,
          status: "error",
          message,
        });
      } finally {
        setListingProgress({ current: i + 1, total });
      }
    }

    setIsListingInProgress(false);
    setListingSummaryItems(results);
    setListingSummaryOpen(true);

    await refresh();
  };

  const handleBulkPriceIncrease = async () => {
    if (!selectedIds.length) return alert("Select at least one row.");
    const pctStr = prompt("Increase price by % (e.g. 10 for +10%)");
    if (!pctStr && pctStr !== "0") return;
    const pct = Number(pctStr);
    if (!Number.isFinite(pct)) return alert("Invalid percentage");

    try {
      const res = await bulkPriceIncrease(selectedIds, pct);
      alert(
        `Updated prices by ${pct}% on ${
          res?.updated ?? selectedIds.length
        } listing(s).`
      );
      await refresh();
    } catch (e) {
      alert(
        `Bulk price update failed: ${e?.response?.data?.error || e.message}`
      );
    }
  };

  const handleBulkCondition = async () => {
    if (!selectedIds.length) return alert("Select at least one row.");
    const cond = prompt(
      "Set condition (e.g. 'Near Mint', 'Lightly Played', 'Damaged')"
    );
    if (!cond) return;

    try {
      const res = await bulkUpdateCondition(selectedIds, cond);
      alert(
        `Updated condition on ${
          res?.updated ?? selectedIds.length
        } listing(s) to '${cond}'.`
      );
      await refresh();
    } catch (e) {
      alert(
        `Bulk condition update failed: ${e?.response?.data?.error || e.message}`
      );
    }
  };

  // ===== Selection from DataGrid (v8 model) =====
  const handleSelectionChange = (selectionModel) => {
    const idsArray = Array.from(selectionModel?.ids ?? []);
    console.log("Selected IDs:", idsArray);
    setSelectedIds(idsArray);
  };

  // ===== Edit dialog handlers =====
  const handleOpenEditDialog = (row) => {
    if (!row?.id) return;
    setEditRowId(row.id);
    setEditTitle(row.listing_title || "");
    setEditPrice(row.listing_price ?? "");
    setEditCondition(row.listing_condition || "");
    setEditListingFormat(row.listing_format || "FIXED_PRICE");
    setEditListingDuration(row.listing_duration || (row.listing_format === "AUCTION" ? "DAYS_3" : "GTC"));
    setEditDialogOpen(true);
  };

  const handleOpenTrainerDialog = async (row) => {
    if (!row?.id) return;
    setTrainerDialogRow(row);
    setTrainerListingRole(row?.trainer_inventory_listing_type || "single");
    setTrainerCardVariant(inferCardVariantFromListing(row));
    setSelectedTrainerInventoryId(
      row?.trainer_inventory_playset_id || row?.trainer_inventory_single_id || ""
    );
    setCreateNewTrainerRecord(!(row?.trainer_inventory_playset_id || row?.trainer_inventory_single_id));
    setTrainerInventoryNotes("");
    setTrainerDialogError("");
    setTrainerDialogSaving(false);
    try {
      const options = await fetchTrainerInventoryOptions();
      setTrainerInventoryOptions(Array.isArray(options) ? options : []);
    } catch (e) {
      setTrainerInventoryOptions([]);
      setTrainerDialogError(e?.response?.data?.error || e.message || "Failed to load Trainer Inventory rows");
    }
    setTrainerDialogOpen(true);
  };

  const handleCloseTrainerDialog = () => {
    if (trainerDialogSaving) return;
    setTrainerDialogOpen(false);
    setTrainerDialogRow(null);
    setTrainerDialogError("");
  };

  const handleSubmitTrainerDialog = async () => {
    if (!trainerDialogRow?.id) return;
    setTrainerDialogSaving(true);
    setTrainerDialogError("");
    try {
      const payload = {
        listing_role: trainerListingRole,
        create_new: createNewTrainerRecord,
        inventory_notes: trainerInventoryNotes,
        card_variant: trainerCardVariant,
      };
      if (!createNewTrainerRecord && selectedTrainerInventoryId) {
        payload.trainer_inventory_id = Number(selectedTrainerInventoryId);
      }
      await markListingAsTrainer(trainerDialogRow.id, payload);
      setTrainerDialogOpen(false);
      await refresh();
    } catch (e) {
      setTrainerDialogError(e?.response?.data?.error || e.message || "Failed to link listing to Trainer Inventory");
    } finally {
      setTrainerDialogSaving(false);
    }
  };

  const handleSubmitEditDialog = async () => {
    if (!editRowId) return;

    // Optimistically update local grid state so the table reflects changes immediately
    setRows((prev) =>
      Array.isArray(prev)
        ? prev.map((r) => {
            if (Number(r?.id) !== Number(editRowId)) return r;
            const parsedPrice = Number.isFinite(Number(editPrice)) ? Number(editPrice) : r?.listing_price;
            return {
              ...r,
              listing_title: editTitle,
              listing_price: parsedPrice,
              listing_condition: editCondition,
              listing_format: editListingFormat,
              listing_duration: editListingDuration,
              // Back-compat keys used by some grid configs
              format: editListingFormat,
              duration: editListingDuration,
            };
          })
        : prev
    );

    await updateListingFromModal(
      editRowId,
      editTitle,
      editPrice,
      editCondition,
      editListingFormat,
      editListingDuration,
      async () => {
        // Ensure the latest server-side values are pulled after save
        await refresh();
      }
    );
    setEditDialogOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* ---- Filter + Action bar ---- */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="PO Filter"
            value={poFilter}
            onChange={(e) => setPoFilter(e.target.value)}
            sx={{ width: 140 }}
          />

          <Stack direction="row" spacing={1}>
            {["To Print", "Photo'ing", "Ready to List", "Live", "Out of Stock", "All"].map(
              (t) => (
                <Button
                  key={t}
                  variant={activeTab === t ? "contained" : "outlined"}
                  onClick={() => setActiveTab(t)}
                >
                  {t}
                </Button>
              )
            )}
          </Stack>

          <TextField
            size="small"
            label="Search (Title/SKU)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            onClick={handleListSelected}
            disabled={!selectedIds.length || isListingInProgress}
          >
            LIST SELECTED
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkPriceIncrease}
            disabled={!selectedIds.length}
          >
            INCREASE PRICES %
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkCondition}
            disabled={!selectedIds.length}
          >
            SET CONDITION
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              runEbayPullSync({
                limit: 25,
                filterMode: "stale_listed",
                label: "Sync 25 stale active listings from eBay",
              })
            }
            disabled={isEbayPullSyncing}
          >
            SYNC 25 ACTIVE
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              runEbayPullSync({
                limit: 50,
                filterMode: "listed",
                label: "Sync 50 active listed listings from eBay",
              })
            }
            disabled={isEbayPullSyncing}
          >
            SYNC 50 LISTED
          </Button>
          <Button
            variant="outlined"
            onClick={handleSyncSelectedFromEbay}
            disabled={!selectedIds.length || isEbayPullSyncing}
          >
            SYNC SELECTED
          </Button>
          <Button variant="text" onClick={refresh}>
            RELOAD
          </Button>
        </Stack>
      </Stack>

      {ebayPullSyncMessage ? (
        <Alert severity={isEbayPullSyncing ? "info" : "success"}>
          {ebayPullSyncMessage}
        </Alert>
      ) : null}

      {activeTab === "Out of Stock" ? (
        <Alert severity="info">
          This filter shows listings already marked Out of Stock plus local Listed rows where eBay quantity is 0 or eBay status is OUT_OF_STOCK. The Live tab excludes those zero-quantity out-of-stock rows.
        </Alert>
      ) : null}

      {/* ---- Totals for current grid ---- */}
      <Stack direction="row" spacing={4}>
        <Typography variant="body2">
          Total Buy Price (Agreed): ${totalBuyPrice.toFixed(2)}
        </Typography>
        <Typography variant="body2">
          Total Expected Profit: ${totalExpectedProfit.toFixed(2)}
        </Typography>
      </Stack>

      {/* ---- Data grid ---- */}
      <ListingTable
        rows={visibleRows}
        refresh={refresh}
        handleOpenEditDialog={handleOpenEditDialog}
        handleSync={syncListing}
        handleEnd={endListing}
        handleScrapePrice={handleScrapePrice}
        handleApplyLatestScrapedPrice={handleApplyLatestScrapedPrice}
        handleListOnEbay={async (id) => {
          await listOnEbay(id); // per-row LIST keeps alert behavior
          await refresh();
        }}
        handleScrapeMetadata={scrapeMetadata}
        handlePriceOnlyUpdate={async (id) => {
          await updatePriceOnly(id);
          await refresh();
        }}
        handleMarkAsTrainer={handleOpenTrainerDialog}
        onSelectionChange={handleSelectionChange}
      />

      {/* ---- Modal ---- */}
      <ListingModal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={editTitle}
        price={editPrice}
        condition={editCondition}
        listingFormat={editListingFormat}
        listingDuration={editListingDuration}
        setTitle={setEditTitle}
        setPrice={setEditPrice}
        setCondition={setEditCondition}
        setListingFormat={setEditListingFormat}
        setListingDuration={setEditListingDuration}
        onSave={handleSubmitEditDialog}
      />

      <Dialog
        open={trainerDialogOpen}
        onClose={handleCloseTrainerDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Mark Listing as Trainer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {trainerDialogError ? <Alert severity="error">{trainerDialogError}</Alert> : null}
            <Typography variant="body2">
              Listing #{trainerDialogRow?.id} — {trainerDialogRow?.card_name || trainerDialogRow?.listing_title || "Untitled Listing"}
            </Typography>
            <Typography variant="body2">SKU: {trainerDialogRow?.sku || "—"}</Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="trainer-role-label">Listing Role</InputLabel>
              <Select
                labelId="trainer-role-label"
                value={trainerListingRole}
                label="Listing Role"
                onChange={(e) => setTrainerListingRole(e.target.value)}
              >
                <MenuItem value="single">Single</MenuItem>
                <MenuItem value="playset">Playset</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="trainer-variant-label">Variant / Finish</InputLabel>
              <Select
                labelId="trainer-variant-label"
                value={trainerCardVariant}
                label="Variant / Finish"
                onChange={(e) => setTrainerCardVariant(e.target.value)}
              >
                {CARD_VARIANTS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createNewTrainerRecord}
                  onChange={(e) => setCreateNewTrainerRecord(e.target.checked)}
                />
              }
              label="Create new Trainer Inventory record if one does not already exist"
            />
            <FormControl fullWidth size="small" disabled={createNewTrainerRecord}>
              <InputLabel id="trainer-existing-label">Existing Trainer Inventory</InputLabel>
              <Select
                labelId="trainer-existing-label"
                value={selectedTrainerInventoryId}
                label="Existing Trainer Inventory"
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedTrainerInventoryId(nextId);
                  const selectedOption = trainerInventoryOptions.find((option) => String(option.id) === String(nextId));
                  if (selectedOption?.card_variant) {
                    setTrainerCardVariant(selectedOption.card_variant);
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select a Trainer Inventory row</em>
                </MenuItem>
                {trainerInventoryOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    #{option.id} — {option.card_name} / {option.card_variant || "Normal"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Notes (optional)"
              value={trainerInventoryNotes}
              onChange={(e) => setTrainerInventoryNotes(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTrainerDialog} disabled={trainerDialogSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitTrainerDialog}
            disabled={trainerDialogSaving || (!createNewTrainerRecord && !selectedTrainerInventoryId)}
          >
            {trainerDialogSaving ? "Saving..." : "Save Trainer Link"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Listing progress dialog ---- */}
      <Dialog open={isListingInProgress}>
        <DialogTitle>Listing selected items</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Listing {listingProgress.current} of {listingProgress.total}{" "}
              selected item(s) on eBay...
            </Typography>
            <LinearProgress
              variant={listingProgress.total ? "determinate" : "indeterminate"}
              value={
                listingProgress.total
                  ? Math.min(
                      100,
                      Math.round(
                        (listingProgress.current / listingProgress.total) * 100
                      )
                    )
                  : 0
              }
            />
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ---- Listing summary dialog ---- */}
      <Dialog
        open={listingSummaryOpen}
        onClose={() => setListingSummaryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Listing results</DialogTitle>
        <DialogContent dividers>
          {listingSummaryItems.length === 0 ? (
            <Typography variant="body2">
              No listing attempts were made.
            </Typography>
          ) : (
            <Stack component="ul" spacing={1} sx={{ pl: 2, mt: 1 }}>
              {listingSummaryItems.map((item) => (
                <Typography component="li" key={item.id} variant="body2">
                  <strong>{item.title}</strong> —{" "}
                  {item.status === "success" ? "✅ Listed" : "❌ Failed"}
                  {item.message ? `: ${item.message}` : null}
                </Typography>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListingSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
